// src/components/booking/DateTimeStep.jsx
import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { TIME_SLOTS } from "../../config/tenant";
import { generateSlots } from "../../utils/slotUtils";
import { SectionHeader } from "../ui";
import {
  MONTH_NAMES, DAY_NAMES_SHORT,
  getDaysInMonth, getFirstDayOfMonth,
  isSameDay, isPastDayInTz, formatDate, toDateStrInTz,
} from "../../utils/dateUtils";

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export default function DateTimeStep({
  selectedDate, selectedTime,
  onDateSelect, onTimeSelect,
  bookedSlots = [],
  loadingSlots = false,
  barberAvailability = null,
  businessHours = null,
  timezone = "America/New_York",
  onWaitlist = null,
}) {
  const today = new Date();
  const [calYear,  setCalYear]  = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());

  // Returns true if the day is open for business
  function isWorkingDay(date) {
    const dayOfWeek = date.getDay(); // 0=Sun, 6=Sat
    // Check business hours first (tenant-level)
    if (businessHours) {
      const key = DAY_KEYS[dayOfWeek];
      const bh = businessHours[key];
      if (bh && bh.open === false) return false;
    }
    // Then check staff availability
    if (!barberAvailability) return true;
    return !!barberAvailability[dayOfWeek];
  }

  // Time slots for the selected date based on barber's hours
  const timeSlots = useMemo(() => {
    if (!selectedDate || !barberAvailability) return TIME_SLOTS;
    const dayAvail = barberAvailability[selectedDate.getDay()];
    if (!dayAvail) return [];
    return generateSlots(dayAvail.start, dayAvail.end);
  }, [selectedDate, barberAvailability]);

  function prevMonth() {
    if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); }
    else setCalMonth(m => m - 1);
  }
  function nextMonth() {
    if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); }
    else setCalMonth(m => m + 1);
  }
  function handleDayClick(day) {
    const d = new Date(calYear, calMonth, day);
    if (isPastDayInTz(d, timezone) || !isWorkingDay(d)) return;
    onDateSelect(d);
    onTimeSelect(null);
  }

  const daysInMonth = getDaysInMonth(calYear, calMonth);
  const firstDay    = getFirstDayOfMonth(calYear, calMonth);

  return (
    <div>
      <SectionHeader title="Select Date & Time" subtitle="Pick an available day and time slot" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Calendar ── */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-5">
            <button onClick={prevMonth}
              className="w-9 h-9 rounded-xl border border-surface-200 flex items-center justify-center
                         text-ink-400 hover:border-brand-300 hover:text-brand-600 transition-all bg-white shadow-sm">
              <ChevronLeft size={16} />
            </button>
            <span className="font-semibold text-ink-800 text-base">
              {MONTH_NAMES[calMonth]} {calYear}
            </span>
            <button onClick={nextMonth}
              className="w-9 h-9 rounded-xl border border-surface-200 flex items-center justify-center
                         text-ink-400 hover:border-brand-300 hover:text-brand-600 transition-all bg-white shadow-sm">
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAY_NAMES_SHORT.map(d => (
              <div key={d} className="text-center text-ink-400 text-xs font-mono py-1">{d}</div>
            ))}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7 gap-1">
            {Array(firstDay).fill(null).map((_, i) => <div key={`e-${i}`} />)}
            {Array(daysInMonth).fill(null).map((_, i) => {
              const day     = i + 1;
              const date    = new Date(calYear, calMonth, day);
              const past    = isPastDayInTz(date, timezone);
              const off     = !isWorkingDay(date);
              const blocked = past || off;
              const isTod   = isSameDay(date, today);
              const isSel   = isSameDay(date, selectedDate);

              return (
                <button key={day} disabled={blocked} onClick={() => handleDayClick(day)}
                  className={`h-9 rounded-xl text-sm font-mono transition-all duration-150
                    ${blocked
                      ? "text-surface-300 cursor-not-allowed"
                      : isSel
                        ? "bg-brand-500 text-white font-bold shadow-brand"
                        : isTod
                          ? "border-2 border-brand-300 text-brand-700 font-semibold hover:bg-brand-50"
                          : "text-ink-600 hover:bg-brand-50 hover:text-brand-700"
                    }`}>
                  {day}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Time slots ── */}
        <div className="card p-5">
          <div className="text-sm font-semibold text-brand-600 mb-4 min-h-[1.5rem]">
            {selectedDate ? formatDate(selectedDate) : "Select a date first"}
          </div>

          {selectedDate ? (
            <>
              {loadingSlots ? (
                <div className="flex items-center justify-center h-48 gap-2 text-ink-400 text-sm">
                  <Loader2 size={18} className="animate-spin text-brand-400" />
                  Loading availability…
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {timeSlots.length === 0 ? (
                    <p className="col-span-3 text-center text-ink-400 text-sm py-8">
                      No availability on this day.
                    </p>
                  ) : null}
                  {timeSlots.map(slot => {
                    const taken  = bookedSlots.includes(slot);
                    const active = selectedTime === slot;
                    if (taken && onWaitlist) {
                      return (
                        <button key={slot} onClick={() => onWaitlist(slot)}
                          className="py-2 rounded-xl text-[10px] font-sans border border-amber-200 text-amber-600 bg-amber-50 hover:bg-amber-100 transition-all duration-150 flex flex-col items-center gap-0.5 leading-tight">
                          <span className="font-mono line-through text-surface-400">{slot}</span>
                          <span className="font-semibold">Waitlist</span>
                        </button>
                      );
                    }
                    return (
                      <button key={slot} disabled={taken} onClick={() => !taken && onTimeSelect(slot)}
                        className={`py-2.5 rounded-xl text-xs font-mono border transition-all duration-150
                          ${taken
                            ? "border-surface-200 text-surface-300 line-through cursor-not-allowed bg-surface-50"
                            : active
                              ? "bg-brand-500 border-brand-500 text-white font-bold shadow-brand"
                              : "border-surface-200 text-ink-600 bg-white hover:border-brand-300 hover:text-brand-600 hover:bg-brand-50 shadow-sm"
                          }`}>
                        {slot}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Legend */}
              {!loadingSlots && (
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-surface-100">
                  {[
                    { color: "bg-brand-500", label: "Selected" },
                    { color: "bg-white border border-surface-200", label: "Available" },
                    { color: "bg-amber-50 border border-amber-200", label: "Waitlist" },
                  ].map(({ color, label }) => (
                    <span key={label} className="flex items-center gap-1.5 text-xs text-ink-400 font-sans">
                      <span className={`w-3 h-3 rounded-sm inline-block ${color}`} /> {label}
                    </span>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-48 text-ink-300 text-sm">
              👈 Pick a date to see available times
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
